import { ICustomerName } from "../models/ICustomer";

// SAV only: the customer the whole dashboard is being viewed as. Kept in sessionStorage so it survives a refresh
// but is scoped to the tab. The API only honours the header for SAV tokens, so this is never a way in on its own.
export const impersonationStorageKey = "wilma-impersonate-customer";
export const impersonationHeader = "X-Impersonate-Customer";

// A link opened in a new tab starts with empty sessionStorage, so the tab last used also leaves its choice here
// for new tabs to pick up when they load. Tabs that are already open never read it, so each keeps its own customer.
const lastUsedStorageKey = "wilma-impersonate-customer-last-used";

// Core's "View on Widget Dashboard" links a lead that belongs to the user's own customer, so it sends this to
// drop any impersonation; otherwise the lead is requested as the impersonated customer and never loads.
export const stopImpersonatingParam = "stopImpersonating";

export function getStoredImpersonation(): ICustomerName | null {
  try {
    const raw = sessionStorage.getItem(impersonationStorageKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as ICustomerName | null;
    return parsed?.id ? parsed : null;
  } catch {
    return null;
  }
}

/** On load: a new tab (nothing in sessionStorage yet, not even "null") takes the last used tab's customer. */
export function restoreImpersonation(): ICustomerName | null {
  if (new URLSearchParams(window.location.search).get(stopImpersonatingParam) === "true") {
    storeImpersonation(null);
    return null;
  }

  try {
    if (sessionStorage.getItem(impersonationStorageKey) === null) {
      const lastUsed = localStorage.getItem(lastUsedStorageKey);
      if (lastUsed !== null) sessionStorage.setItem(impersonationStorageKey, lastUsed);
    }
  } catch {}

  return getStoredImpersonation();
}

/** Called when a tab gains focus, so a link opened from it in a new tab carries on as the same customer. */
export function rememberTabImpersonation() {
  try {
    const raw = sessionStorage.getItem(impersonationStorageKey);
    if (raw !== null) localStorage.setItem(lastUsedStorageKey, raw);
  } catch {}
}

export function storeImpersonation(customer: ICustomerName | null) {
  try {
    // "null" rather than removing, so a refresh of a tab switched back to SAV isn't mistaken for a new tab
    const raw = JSON.stringify(customer);
    sessionStorage.setItem(impersonationStorageKey, raw);
    localStorage.setItem(lastUsedStorageKey, raw);
  } catch {}
}

const fallbackColour = "#D97706";

/** The customer's brand colour if it's a usable hex, otherwise a warning amber so impersonation is never subtle. */
export function impersonationColour(customer: ICustomerName | null) {
  const colour = customer?.primaryColour?.trim();
  return colour && /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(colour)
    ? colour.startsWith("#") ? colour : `#${colour}`
    : fallbackColour;
}

/** Background + readable text colour for the navbars while impersonating, or null when not. */
export function impersonationTheme(customer: ICustomerName | null | undefined) {
  if (!customer) return null;

  const background = impersonationColour(customer);
  return { background, text: contrastTextColour(background) };
}

/** Black or white, whichever reads better on the given hex background. */
export function contrastTextColour(hex: string) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");

  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return luminance > 0.6 ? "#111827" : "#FFFFFF";
}
